import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { State } from "../../../reducer";
import { getSettings, patchSettings } from "../../../api/settings.api";
import { LayoutAdminCafe } from "../../../layout/layout_cafe";

interface StepperProps {
  emoji: string;
  title: string;
  desc: string;
  unit: string;
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  dirty?: boolean;
}

function StepperCard({
  emoji, title, desc, unit, value, onChange, dirty, min = 1, max = 50,
}: StepperProps) {
  return (
    <section className="rounded-3xl border border-ink-100 bg-white shadow-soft p-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-ink-100 flex items-center justify-center text-2xl shrink-0" aria-hidden>
          {emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-black text-base text-ink-1000">{title}</h2>
            {dirty && (
              <span className="inline-flex items-center gap-1 px-2 h-[18px] rounded-full text-[10px] font-bold bg-amber-100 text-amber-900">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Unsaved
              </span>
            )}
          </div>
          <p className="text-xs text-ink-500 mt-0.5">{desc}</p>
        </div>
      </div>

      {/* Plain number input — type the value directly. */}
      <div className="mt-5 flex items-center gap-3">
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            onChange(Number.isNaN(v) ? 0 : v);
          }}
          className="flex text-center w-18 h-30 px-5 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none font-display font-black text-3xl text-ink-1000 transition-colors"
        />
        <span className="flex-1 text-sm font-semibold text-center text-ink-500 whitespace-nowrap">{unit}</span>
      </div>
    </section>
  );
}

const isValidLimit = (v: number) => Number.isInteger(v) && v >= 1 && v <= 50;

function AppSettingsInner() {
  const role = useSelector((s: State) => s.user?.role);
  const navigate = useNavigate();

  const [cups, setCups] = useState<number>(2);
  const [desserts, setDesserts] = useState<number>(2);
  const [savedCups, setSavedCups] = useState<number>(2);
  const [savedDesserts, setSavedDesserts] = useState<number>(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (role && role !== "dev") {
      navigate("/unauthorized", { replace: true });
      return;
    }
    getSettings()
      .then((s) => {
        setCups(s.dailyOrderLimit);
        setSavedCups(s.dailyOrderLimit);
        setDesserts(s.dailyDessertLimit);
        setSavedDesserts(s.dailyDessertLimit);
        setUpdatedAt(s.updatedAt);
      })
      .finally(() => setLoading(false));
  }, [role, navigate]);

  const cupsDirty = cups !== savedCups;
  const dessertsDirty = desserts !== savedDesserts;
  const dirty = cupsDirty || dessertsDirty;
  const valid = isValidLimit(cups) && isValidLimit(desserts);

  const handleSave = async () => {
    if (!valid || !dirty) return;
    setSaving(true);
    try {
      const fresh = await patchSettings({
        dailyOrderLimit: cups,
        dailyDessertLimit: desserts,
      });
      setSavedCups(fresh.dailyOrderLimit);
      setSavedDesserts(fresh.dailyDessertLimit);
      setUpdatedAt(fresh.updatedAt);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCups(savedCups);
    setDesserts(savedDesserts);
  };

  return (
    <div className="min-h-screen bg-ink-50 font-sans pb-[140px]">
      {/* Hero header with subtle dark band — sets a "control panel" tone. */}
      <header className="relative bg-ink-1000 text-ink-0 px-5 pt-[28px] pb-[28px] overflow-hidden">
        <div
          className="absolute -right-12 -top-12 w-[200px] h-[200px] rounded-full opacity-[0.08] pointer-events-none"
          style={{ background: "var(--festive-1, #ffffff)" }}
        />
        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-5xl opacity-20" aria-hidden>⚙️</div>
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-ink-0/60 mb-1">Developer</p>
        <h1 className="font-display font-black text-[32px] leading-tight tracking-tightest">
          App settings
        </h1>
        <p className="text-sm text-ink-0/70 mt-2 max-w-md">
          Global tunables that affect every user across the app.
          Only <span className="font-bold text-ink-0">dev</span> can edit these.
        </p>
      </header>

      <main className="px-5 pt-[20px] space-y-3 max-w-2xl mx-auto">
        {loading ? (
          <div className="py-[80px] flex justify-center">
            <svg className="w-7 h-7 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
        ) : (
          <>
            <p className="eyebrow-v2 mt-3 mb-1 px-1">Order limits</p>

            <StepperCard
              emoji="☕"
              title="Daily order limit (drinks)"
              desc="How many cups each user can order per day."
              unit="cups per day"
              value={cups}
              onChange={setCups}
              dirty={cupsDirty}
            />

            <StepperCard
              emoji="🍰"
              title="Daily dessert limit"
              desc="Total desserts per user per day. Each menu still capped at 1/day."
              unit="desserts per day"
              value={desserts}
              onChange={setDesserts}
              dirty={dessertsDirty}
            />

            {!valid && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-xs text-rose-800 font-semibold">
                Limits must be whole numbers between 1 and 50.
              </div>
            )}

            {updatedAt && (
              <p className="pt-3 px-1 text-[11px] text-ink-400">
                Last updated {moment(updatedAt).fromNow()}
                <span className="text-ink-300"> · </span>
                {moment(updatedAt).format("D MMM YYYY · HH:mm")}
              </p>
            )}
          </>
        )}
      </main>

      {/* Sticky save bar — slides into view only when there are unsaved changes. */}
      {dirty && !loading && (
        <div
          className="fixed bottom-0 left-0 right-0 md:left-[280px] z-30 bg-white/95 backdrop-blur-md border-t border-ink-200 px-5 py-3 shadow-floaty animate-slide-up"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <p className="flex-1 min-w-0 text-xs text-ink-700 truncate">
              <span className="font-bold text-ink-1000">Unsaved changes</span>
              <span className="text-ink-500 ml-2">
                {cupsDirty && `Drinks: ${savedCups} → ${cups}`}
                {cupsDirty && dessertsDirty && " · "}
                {dessertsDirty && `Desserts: ${savedDesserts} → ${desserts}`}
              </span>
            </p>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className="btn-v2-secondary btn-v2-sm shrink-0"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!valid || saving}
              className="btn-v2-primary btn-v2-sm shrink-0 disabled:opacity-40"
            >
              {saving ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  Saving
                </>
              ) : (
                <>
                  Save changes
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default function AppSettingsPage() {
  return (
    <LayoutAdminCafe>
      <AppSettingsInner />
    </LayoutAdminCafe>
  );
}
